import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { seoAiVisibility, seoRunAiVisibility } from "@/lib/seo/seo-os.functions";
import { SeoPage, Stat, Panel, Pill, Loading, Empty, DataTable, num, pct, stateLabel, stateTone } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/seo/ai-visibility")({
  head: () => ({ meta: [{ title: "AI visibility · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: AiVisibilityPage,
});

function AiVisibilityPage() {
  const load = useServerFn(seoAiVisibility);
  const run = useServerFn(seoRunAiVisibility);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["seo-ai-visibility"], queryFn: () => load() });
  const d = q.data as any;
  const latest = (d?.latest ?? []) as any[];
  const prompts = new Map(((d?.prompts ?? []) as any[]).map((p) => [p.id, p]));

  const sweep = useMutation({
    mutationFn: () => run({}),
    onSuccess: (r: any) => {
      toast.success(`Checked ${r.checked} questions — mentioned in ${r.mentioned}.`);
      qc.invalidateQueries({ queryKey: ["seo-ai-visibility"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "The check could not run."),
  });

  return (
    <SeoPage
      title="AI visibility"
      description="Whether AI answer engines mention or cite you when investors ask financing questions — and which competitors they name instead."
      actions={
        <Button onClick={() => sweep.mutate()} disabled={sweep.isPending}>
          {sweep.isPending ? "Checking…" : "Check now"}
        </Button>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : latest.length === 0 ? (
        <Empty>No checks run yet.</Empty>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Questions tracked" value={num(latest.length)} />
            <Stat label="We get mentioned" value={pct(d.mentionRate)} />
            <Stat label="Source status" value={stateLabel(d.state)} />
          </div>
          <Panel title="Latest results">
            <DataTable
              head={["Question", "Mentioned", "Cited pages", "Competitors named", "Status"]}
              rows={latest.map((r) => [
                prompts.get(r.prompt_id)?.prompt ?? "—",
                <Pill key={`${r.id}m`} tone={r.mentioned ? "good" : "warn"}>
                  {r.mentioned ? "Yes" : "No"}
                </Pill>,
                (r.cited_urls ?? []).join(", ") || "—",
                (r.competitors ?? []).join(", ") || "—",
                <Pill key={`${r.id}s`} tone={stateTone(r.provider_state)}>
                  {stateLabel(r.provider_state)}
                </Pill>,
              ])}
            />
          </Panel>
        </>
      )}
    </SeoPage>
  );
}
